// 변경 후 금액을 지급회차에 다시 싣는다 — 지급 회차는 그대로, 미지급 회차에 비율(ratio_bp) 가중으로 나눈다.

import type { ContractPayment } from "../contract/rows";
import { checked } from "../money/checked";
import { BelowPaidError } from "./errors";

// 미지급 회차가 없는데 늘어난 금액 — 이 이름의 회차를 끝에 새로 붙인다(RPC 는 받은 그대로 넣기만).
export const CHANGE_ORDER_SETTLEMENT_LABEL = "변경공사 정산";

export interface NewPaymentRow {
  seq: number;
  label: string;
  ratio_bp: number;
  trigger_text: string;
}

export interface ReallocatedPayment {
  id: string;
  amount: number;
  // 있으면 새로 넣을 회차 — 없으면 기존 회차의 금액만 바꾼다.
  new?: NewPaymentRow;
}

type PaymentLike = Pick<ContractPayment, "id" | "seq" | "ratio_bp" | "amount" | "paid_at">;

export const reallocatePayments = (
  payments: readonly PaymentLike[],
  newTotal: number,
  newId: () => string
): ReallocatedPayment[] => {
  checked(newTotal, "변경 후 금액");
  const sorted = [...payments].sort((a, b) => a.seq - b.seq);
  const unchanged = sorted.map((p) => ({ id: p.id, amount: p.amount }));
  const currentSum = checked(
    sorted.reduce((a, p) => a + p.amount, 0),
    "회차 합계"
  );
  // 증감 0(공기 연장·기록용)이면 회차를 쓰지 않는다.
  if (newTotal === currentSum) return unchanged;

  const paidSum = checked(
    sorted.filter((p) => p.paid_at !== null).reduce((a, p) => a + p.amount, 0),
    "기지급 합계"
  );
  const unpaidTotal = newTotal - paidSum;
  if (unpaidTotal < 0) throw new BelowPaidError(paidSum, newTotal);

  const unpaid = sorted.filter((p) => p.paid_at === null);
  if (unpaid.length === 0) {
    if (unpaidTotal === 0) return unchanged;
    const seq = sorted.reduce((m, p) => Math.max(m, p.seq), 0) + 1;
    const ratio_bp = Math.min(10000, Math.max(1, Math.round(checked(unpaidTotal * 10000, "정산 회차 비율") / newTotal)));
    const label = CHANGE_ORDER_SETTLEMENT_LABEL;
    return [...unchanged, { id: newId(), amount: unpaidTotal, new: { seq, label, ratio_bp, trigger_text: label } }];
  }

  const weight = unpaid.reduce((a, p) => a + p.ratio_bp, 0);
  const shares = new Map<string, number>();
  let assigned = 0;
  for (const p of unpaid) {
    const share = weight > 0 ? Math.floor(checked(unpaidTotal * p.ratio_bp, "회차 금액") / weight) : 0;
    shares.set(p.id, share);
    assigned += share;
  }
  // 끝수는 마지막 미지급 회차가 가져간다(splitPaymentAmounts 와 같은 규칙).
  const last = unpaid[unpaid.length - 1];
  shares.set(last.id, (shares.get(last.id) ?? 0) + unpaidTotal - assigned);

  return sorted.map((p) => ({
    id: p.id,
    amount: p.paid_at !== null ? p.amount : (shares.get(p.id) ?? 0),
  }));
};
